import { CSSProperties, FC, SyntheticEvent, useContext, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { ReactCompareSlider, ReactCompareSliderHandle, ReactCompareSliderImage } from 'react-compare-slider';
import { AppContext } from '../contexts/AppContext';
import { getTranslation } from '../common/Translation';
import { parseCredits } from '../common/Media';
import { ImageCaption, ImageCredits } from './BlockMedia_Slideshow';
import { fetchMedia } from '../services/Api';
import BlockContainer from './BlockContainer';

interface BlockImageCompareProps {
  elements?: any,
  /** contain: height follows the image like the "full" layout. cover: fixed height like the slideshow */
  objectFit?: "cover" | "contain"
}

const BlockContain = styled(BlockContainer)`
  flex-direction: column;

  & > figure {
    width: 100%;
    margin: 0;
  }

  & > figure:not(:last-of-type) {
    margin-bottom: 30px;
  }

  & [data-rcs="root"] {
    width: 100%;
    border-radius: var(--block-radius);
  }
`

/*
 * Both captions share one grid cell, so the block always has the height of the longest caption
 * and the content below never moves when the caption changes. Only the active one is visible.
 */
const CaptionStack = styled.div`
  display: grid;

  & > div {
    grid-area: 1 / 1;
    /* Contain the floated caption and credit lines */
    display: flow-root;
    transition: opacity .25s;
  }

  & > div.hidden {
    opacity: 0;
    visibility: hidden;
    transition: opacity .25s, visibility 0s .25s;
  }
`;

/** Show the caption of the image you see at least this much of (percent of the width) */
const CAPTION_SWITCH_PERCENT = 60;

/**
 * Caption for one image, like the slideshow: title – description, and credits below
 * @returns {JSX.Element} Component template
 */
const CompareCaption: FC<{ media: any, language?: string, hidden?: boolean }> = ({media, language, hidden}) => {
  const title = getTranslation(media?.title, language);
  const description = getTranslation(media?.description, language);
  const credits = parseCredits(media?.credits, language);

  return (
    <div className={hidden ? "hidden" : undefined} aria-hidden={hidden}>
      {(title || description) && (
        <ImageCaption>
          {title && (<strong>{title}</strong>)}
          {title && description && (<> – </>)}
          {description}
        </ImageCaption>
      )}
      {credits && (<ImageCredits>{credits}</ImageCredits>)}
    </div>
  );
};

/**
 * Size of the compare slider:
 * - contain: like the "full" media layout. Height follows the image's aspect ratio, between 35vh and 75vh.
 * - cover: like the slideshow. Fixed height of 40vw, between 20vh and 75vh, and the images fill it.
 */
const getSliderStyle = (objectFit: "cover" | "contain", aspectRatio?: string): CSSProperties => {
  if (objectFit === "contain") {
    // Aspect ratio of the first image, known when it has loaded (the media API "dimension" is not the real size)
    // Same subtle tone behind letterboxed images as the slideshow (Image component default)
    const backgroundColor = "#333";
    return aspectRatio
      ? { aspectRatio, minHeight: "35vh", maxHeight: "75vh", backgroundColor }
      : { height: "clamp(35vh, 50vw, 75vh)", backgroundColor };
  }
  return { height: "clamp(20vh, 40vw, 75vh)" };
};

// Drag handle in the theme's accent color
const CompareHandle: FC<{ color?: string }> = ({ color }) => (
  <ReactCompareSliderHandle buttonStyle={{ alignItems: "center", backgroundColor: color || "#3F7DF0", border: "none"}}/>
)

interface ComparePairProps {
  left: any,
  right: any,
  objectFit: "cover" | "contain",
  accentColor?: string,
  language?: string
}

/**
 * One compare slider with a caption below it (title, description and credits, like the slideshow).
 * The caption follows the image you see most of: it switches when at least 60% of an image is visible,
 * and keeps the previous one in between (40–60%), so it does not flicker around the middle.
 * @returns {JSX.Element} Component template
 */
const ComparePair: FC<ComparePairProps> = ({left, right, objectFit, accentColor, language}) => {
  const [aspectRatio, setAspectRatio] = useState<string>();
  // Which caption is shown: the left (first) image at start
  const [activeSide, setActiveSide] = useState<"left" | "right">("left");

  // Only react after the user has touched the slider: the slider can report a wrong position while it is laid out
  const interacted = useRef<boolean>(false);
  const onInteract = () => { interacted.current = true; };

  // position = divider in percent from the left = how much of the left image is visible
  const onPositionChange = (position: number) => {
    if (!interacted.current) { return; }
    setActiveSide(previous => {
      if (position >= CAPTION_SWITCH_PERCENT) { return "left"; }
      if (position <= 100 - CAPTION_SWITCH_PERCENT) { return "right"; }
      return previous;
    });
  };

  // dimension=1600 limits the longest side only; "1200x1200" returned a square image and hid the real aspect ratio
  // With contain, each image gets the background itself: the letterbox area of the top image would otherwise be
  // transparent and show the other image through it when the two images have different aspect ratios
  const imageStyle: CSSProperties = objectFit === "contain" ? { objectFit, backgroundColor: "#333" } : { objectFit };
  const onFirstImageLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    if (naturalWidth > 0 && naturalHeight > 0) {
      setAspectRatio(`${naturalWidth} / ${naturalHeight}`);
    }
  };

  return (
    <figure onPointerDownCapture={onInteract} onKeyDownCapture={onInteract} onTouchStartCapture={onInteract}>
      <ReactCompareSlider handle={<CompareHandle color={accentColor}/>} style={getSliderStyle(objectFit, aspectRatio)}
        onPositionChange={onPositionChange}
        itemOne={<ReactCompareSliderImage src={(left?.src || left?.url) + "?dimension=1600"} alt={getTranslation(left?.title, language)} style={imageStyle} onLoad={onFirstImageLoad}/>}
        itemTwo={<ReactCompareSliderImage src={(right?.src || right?.url) + "?dimension=1600"} alt={getTranslation(right?.title, language)} style={imageStyle}/>} />
      <CaptionStack>
        <CompareCaption media={left} language={language} hidden={activeSide !== "left"}/>
        <CompareCaption media={right} language={language} hidden={activeSide !== "right"}/>
      </CaptionStack>
    </figure>
  );
};

/**
 * Render an error message. Supports markdown
 * @returns {JSX.Element} Component template
 */
const BlockImageCompare: FC<BlockImageCompareProps> = ({elements, objectFit = "cover"}) => {
  const { currentLanguage } = useContext(AppContext);
  const theme: any = useTheme();
  const [imageList, setImageList] = useState<any>([]);

  // Get media objects from API
  useEffect(() => {
    let isSubscribed = true;
    let imageArray: any[] = [];

    (async() => {
      await Promise.all(elements?.map(async (element: any, i: number) => {
        if (isSubscribed) {
          imageArray[i] = await fetchMedia(String(element?.mediaId));
          if (imageArray[i]?.media_type === "video") { 
            imageArray[i].src = imageArray[i]?.thumbnail_src
          }
        }
      }));

      setImageList(imageArray);
    })();

    return () => { isSubscribed = false };
  }, [elements]);

  // Render list of compare sliders, two images per slider
  const pairs: { left: any, right: any }[] = [];
  for (let i = 0; i + 1 < imageList.length; i += 2) {
    if (imageList?.[i] && imageList?.[i+1]) {
      pairs.push({ left: imageList[i], right: imageList[i+1] });
    }
  }

  // Return null if ResizeObserver is not supported. Relevant to iOS versions older than v13.4
  if (typeof ResizeObserver === "undefined") {
    return null;
  }

  return (
    <BlockContain>
      {pairs.map((pair, i) => (
        <ComparePair key={`compare${i}`} left={pair.left} right={pair.right} objectFit={objectFit} accentColor={theme?.accentColor} language={currentLanguage}/>
      ))}
    </BlockContain>
  );
}

export default BlockImageCompare;